// useLiveRefresh — živé obnovenie dát cez SSE (ústava 348 /api/events + backend broadcast po každej mutácii).
// Backend pošle „refresh" po každej úspešnej zmene; tu sa na to napojíme a CIELENE znova načítame dáta pohľadu.
// GUARD: ak práve edituješ (busy/dirty), re-fetch sa preskočí — nestratíš rozrobenú prácu (ústava 2087 unsaved-guard).
// Jeden zdieľaný EventSource pre celý Matrix; viac pohľadov sa môže prihlásiť.
import { useEffect, useRef } from 'react'
import { isAnyDirty } from './unsavedGuard.js'

let _es = null
const _subs = new Set()
let _debounce = null

let _undoHooked = false
// coalesce nával zmien do jedného re-fetchu (~700 ms)
function fire() {
  if (_debounce) clearTimeout(_debounce)
  _debounce = setTimeout(() => { for (const fn of _subs) { try { fn() } catch (_) {} } }, 700)
}

function ensureStream() {
  if (!_undoHooked && typeof window !== 'undefined') {
    _undoHooked = true
    // V3: po Ctrl+Z (UndoManager → udalosť matrix-undo-applied) obnov obrazovku hneď, aj keď SSE mešká alebo nebeží
    window.addEventListener('matrix-undo-applied', fire)
  }
  if (_es || typeof window === 'undefined' || typeof EventSource === 'undefined') return
  try {
    _es = new EventSource('/api/events')
    _es.onmessage = fire
    _es.onerror = () => { /* prehliadač sa sám pokúsi reconnectnúť */ }
  } catch (_) { _es = null }
}

/**
 * @param {Function} onRefresh  čo spraviť pri cudzej zmene (zvyčajne re-fetch dát pohľadu)
 * @param {Function} [canRefresh]  vráť false ak sa NEMÁ obnoviť (práve edituješ / dirty / otvorený modal)
 */
export default function useLiveRefresh(onRefresh, canRefresh) {
  const cbRef = useRef(onRefresh)
  const guardRef = useRef(canRefresh)
  cbRef.current = onRefresh
  guardRef.current = canRefresh
  useEffect(() => {
    ensureStream()
    let missed = false
    const run = () => {
      if (isAnyDirty()) return // GLOBÁLNA poistka: hocikde rozrobená neuložená editácia → nerušiť (ústava 2087)
      if (typeof guardRef.current === 'function' && !guardRef.current()) return // lokálny guard pohľadu
      try { cbRef.current && cbRef.current() } catch (_) {}
    }
    const sub = () => {
      if (document.hidden) { missed = true; return } // tab v pozadí → zapamätaj, dobehne pri návrate
      run()
    }
    // catch-up: keď sa vrátiš na okno, dotiahni čerstvé dáta (rieši oneskorenie pri prepínaní okien)
    const onVis = () => { if (!document.hidden && missed) { missed = false; run() } }
    document.addEventListener('visibilitychange', onVis)
    _subs.add(sub)
    return () => { _subs.delete(sub); document.removeEventListener('visibilitychange', onVis) }
  }, [])
}
